import React, { useCallback, useEffect, useState } from 'react';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import { AppFormInput } from '../../components/common/appForms';
import Logo from '../../components/common/Logo/Logo';
import { type BreadcrumbItem, useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { FupService } from '../../services';
import { unmaskCpf } from '../../utils/masks';
import { validateEmail } from '../../utils/validation';
import FupQuestionnaireFields from './FupQuestionnaireFields';
import {
  composeFupResponse,
  EMPTY_FUP_QUESTIONNAIRE,
  FUP_CLOSING_LINE,
  FUP_INTRO_LINES,
  validateFupQuestionnaire,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from './fupQuestionnaire';
import styles from './Fup.module.css';

interface FupFormValues {
  studentName: string;
  email: string;
  cpf: string;
}

type FeedbackState = {
  message: string;
  variant: 'success' | 'error';
} | null;

type FieldErrors = {
  studentName?: string;
  email?: string;
  cpf?: string;
} & FupQuestionnaireErrors;

const INITIAL_VALUES: FupFormValues = {
  studentName: '',
  email: '',
  cpf: '',
};

const Fup: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [formValues, setFormValues] = useState<FupFormValues>(INITIAL_VALUES);
  const [answers, setAnswers] = useState<FupQuestionnaireAnswers>(EMPTY_FUP_QUESTIONNAIRE);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'FUP' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const handleFieldChange = useCallback((field: keyof FupFormValues, value: string) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    setFeedbackState(null);
    setErrors((currentErrors) => ({ ...currentErrors, [field]: '' }));
  }, []);

  const handleAnswerChange = useCallback(<K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => {
    setAnswers((current) => ({ ...current, [field]: value }));
    setFeedbackState(null);
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  }, []);

  const validateForm = useCallback((): FieldErrors => {
    const nextErrors: FieldErrors = {};

    const trimmedName = formValues.studentName.trim();
    const trimmedEmail = formValues.email.trim();
    const cpfDigits = unmaskCpf(formValues.cpf);
    if (!trimmedName) {
      nextErrors.studentName = 'Informe o nome da aluna.';
    } else if (trimmedName.length < 3) {
      nextErrors.studentName = 'O nome deve ter pelo menos 3 caracteres.';
    }

    if (!trimmedEmail && !cpfDigits) {
      nextErrors.email = 'Informe o e-mail ou o CPF da aluna.';
      nextErrors.cpf = 'Informe o e-mail ou o CPF da aluna.';
    }

    if (trimmedEmail) {
      const emailError = validateEmail(trimmedEmail);
      if (emailError) {
        nextErrors.email = emailError;
      }
    }

    if (!trimmedEmail && cpfDigits && cpfDigits.length !== 11) {
      nextErrors.cpf = 'Digite um CPF válido com 11 números.';
    }

    return {
      ...nextErrors,
      ...validateFupQuestionnaire(answers),
    };
  }, [answers, formValues.cpf, formValues.email, formValues.studentName]);

  const handleSubmit = useCallback(async () => {
    const nextErrors = validateForm();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const trimmedName = formValues.studentName.trim();
    const trimmedEmail = formValues.email.trim();
    const cpfDigits = unmaskCpf(formValues.cpf);

    setIsSubmitting(true);
    setFeedbackState(null);

    try {
      await FupService.submitFeedback({
        studentName: trimmedName,
        email: trimmedEmail || undefined,
        cpf: trimmedEmail ? undefined : (cpfDigits || undefined),
        feedback: composeFupResponse(answers),
        answers,
      });

      const identifierUsed = trimmedEmail ? `e-mail (${trimmedEmail})` : `CPF (${cpfDigits})`;

      setFeedbackState({
        message: `FUP enviado com sucesso. Identificação usada: ${identifierUsed}.`,
        variant: 'success',
      });
      setFormValues(INITIAL_VALUES);
      setAnswers(EMPTY_FUP_QUESTIONNAIRE);
      setErrors({});
    } catch (error: unknown) {
      setFeedbackState({
        message: error instanceof Error ? error.message : 'Não foi possível enviar o FUP agora.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, formValues, validateForm]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.header}>
          <Logo />
        </div>

        <section className={styles.panelCard}>
          <h1 className={styles.title}>Follow up do próximo ciclo</h1>
          <div className={styles.intro}>
            <p className={styles.introGreeting}>{FUP_INTRO_LINES[0]}</p>
          </div>

          {feedbackState && (
            <AlertBanner
              message={feedbackState.message}
              variant={feedbackState.variant}
              className={styles.feedbackAlert}
            />
          )}

          <div className={styles.formGrid}>
            <AppFormInput
              id="fup-student-name"
              name="studentName"
              label="Nome"
              value={formValues.studentName}
              onChange={(event) => handleFieldChange('studentName', event.target.value)}
              placeholder="Digite seu nome"
              error={errors.studentName}
              disabled={isSubmitting}
              required
            />

            <AppFormInput
              id="fup-email"
              name="email"
              label="E-mail"
              type="email"
              value={formValues.email}
              onChange={(event) => handleFieldChange('email', event.target.value)}
              placeholder="Digite seu email"
              error={errors.email}
              disabled={isSubmitting}
              required
            />

            <AppFormInput
              id="fup-cpf"
              name="cpf"
              label="CPF"
              value={formValues.cpf}
              onChange={(event) => handleFieldChange('cpf', event.target.value)}
              placeholder="Digite seu cpf"
              inputMode="numeric"
              error={errors.cpf}
              disabled={isSubmitting}
            />

            <FupQuestionnaireFields
              answers={answers}
              errors={errors}
              disabled={isSubmitting}
              onChange={handleAnswerChange}
            />
          </div>

          <p className={styles.closing}>{FUP_CLOSING_LINE}</p>

          <div className={styles.footerActions}>
            <Button type="button" variant="primary" size="small" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Enviar feedback'}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Fup;
